import React, { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiCopy, BiHelpCircle } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import HintBlock from '@/components/common/HintBlock';
import { PSButton } from '@/components/ui/button';
import ProBadge from '@/components/ui/ProBadge';
import { louiseBotImageUrl } from '@/constants/dashboard-popover-urls.constant';
import usePermission from '@/hooks/usePermission';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { handleResetAppearance, handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';
import { IAiAppearance } from './types';

const CSS_CLASSES = [
  { name: 'cs_bot_body', descKey: 'cssClass_body', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081657_cs_bot_body.png' },
  { name: 'cs_bot_container', descKey: 'cssClass_bodyBorder', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081496_cs_bot_border.png' },
  { name: 'cs_bot_head', descKey: 'cssClass_head', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081586_cs_bot_head.png' },
  { name: 'cs_bot_head_title', descKey: 'cssClass_headTitle', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081597_cs_bot_head_title.png' },
  { name: 'cs_bot_message_text', descKey: 'cssClass_messageText', image: 'https://cdn.1cdn.app/application/LOUISEBOT/2512061808158_cs_bot_message_text.png' },
  { name: 'cs_bot_send_btn', descKey: 'cssClass_sendBtn', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081512_cs_bot_send_btn.png' },
  { name: 'cs_bot_voice_btn', descKey: 'cssClass_voiceBtn', image: 'https://cdn.1cdn.app/application/LOUISEBOT/25120618081514_cs_bot_voice_btn.png' },
];

const CALLBOT_CSS_CLASSES = [
  { name: 'cs_bot_container', descKey: 'cssClass_bodyBorder', image: louiseBotImageUrl },
  { name: 'cs_bot_message_text', descKey: 'cssClass_messageText', image: louiseBotImageUrl },
];

function AppearanceCustomCss() {
  const { bot_code } = useAppSelector(selectAppearanceState);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [showCssHelp, setShowCssHelp] = useState(false);

  const handleCopyClass = (className: string) => {
    navigator.clipboard.writeText(`.${className} {\n  \n}`);
    toast.success(t('AppearanceCustomCss/copied'));
  };

  const {
    register,
    formState: { errors },
  } = useFormContext<IAiAppearance>();
  const permissions = usePermission();
  return (
    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
      <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-flex tw-items-center">
        {t('AppearanceCustomCss/key1')}
        {!permissions.settingsPage.widget.customization.customCss && (
          <ProBadge
            className="tw-ms-2"
            onClose={() => {
              dispatch(handleResetAppearance());
            }}
          />
        )}
      </div>
      <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
        <Form.Group className="tw-mb-3 tw-w-full">
          <Form.Label htmlFor="customCss">
            <PSButton id="btn-css-help" type="button" variant="secondary" size="sm" onClick={() => setShowCssHelp(true)}>
              <BiHelpCircle size={14} />
              {t('AppearanceCustomCss/viewCssClasses')}
            </PSButton>
          </Form.Label>
          <Form.Control
            as={'textarea'}
            rows={10}
            isInvalid={!!errors.customCss}
            disabled={!permissions.settingsPage.widget.customization.customCss}
            {...register('customCss', {
              onChange: e => {
                dispatch(
                  handleUpdateAppearanceItem({
                    key: 'customCss',
                    value: e.target.value,
                  })
                );
              },
            })}
            placeholder={t('AppearanceCustomCss/key2')}
            onKeyDown={e => {
              if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();

                const { selectionStart, selectionEnd, value } = e.currentTarget;
                if (selectionStart === null || selectionEnd === null) return;

                // Insert newline at cursor position
                const newValue = value.substring(0, selectionStart) + '\n' + value.substring(selectionEnd);

                // Update the textarea manually
                e.currentTarget.value = newValue;

                // Move cursor after the newline
                e.currentTarget.selectionStart = e.currentTarget.selectionEnd = selectionStart + 1;

                // Dispatch update to Redux
                dispatch(
                  handleUpdateAppearanceItem({
                    key: 'customCss',
                    value: newValue,
                  })
                );

                // Also trigger input event so react-hook-form stays in sync
                e.currentTarget.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }}
          />
          <Form.Control.Feedback type="invalid">{errors.customCss?.message as string}</Form.Control.Feedback>
          <HintBlock texts={[t('AppearanceCustomCss/key3')]} />
        </Form.Group>
      </div>

      <Modal size="lg" show={showCssHelp} onHide={() => setShowCssHelp(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="tw-text-lg tw-font-semibold">{t('AppearanceCustomCss/cssClassReference')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-gray-600 tw-mb-4">{t('AppearanceCustomCss/cssHelpDescription')}</p>

          <div className="tw-space-y-3">
            {(bot_code === BotTypeEnum.CALLBOT ? CALLBOT_CSS_CLASSES : CSS_CLASSES).map(cssClass => (
              <div
                key={cssClass.name}
                className="tw-flex tw-gap-4 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 hover:tw-bg-gray-100 tw-transition-colors"
              >
                <img
                  src={cssClass.image}
                  alt={cssClass.name}
                  className="tw-w-24 tw-h-auto tw-rounded tw-border tw-border-gray-300 tw-object-contain tw-flex-shrink-0"
                />
                <div className="tw-flex tw-flex-1 tw-items-center tw-justify-between">
                  <div>
                    <code className="tw-text-sm tw-font-mono tw-text-blue-600 tw-font-medium">.{cssClass.name}</code>
                    <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{t(`AppearanceCustomCss/${cssClass.descKey}`)}</p>
                  </div>
                  <PSButton id={`btn-copy-${cssClass.name}`} type="button" variant="outline" size="sm" onClick={() => handleCopyClass(cssClass.name)}>
                    <BiCopy size={14} />
                    {t('AppearanceCustomCss/copy')}
                  </PSButton>
                </div>
              </div>
            ))}
          </div>

          <div className="tw-mt-4 tw-p-3 tw-bg-gray-100 tw-rounded-lg">
            <p className="tw-text-sm tw-text-gray-700 tw-font-medium tw-mb-2">{t('AppearanceCustomCss/example')}</p>
            <code className="tw-text-sm tw-text-gray-800 tw-block tw-whitespace-pre-wrap">{`.${
              bot_code === BotTypeEnum.CALLBOT ? `cs_bot_message_text` : `cs_bot_head_title`
            } {
  background-color: #333;
  font-size: 18px;
}`}</code>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default WrappedErrorBoundary(AppearanceCustomCss);
